// Typed contracts for the hubx-item-domain v2 API (HUBX-8385).
// Shapes mirror the API exactly (design doc 2615050241 §4.1/§4.2/§8.3; lookups per HUBX-8378).
// Kept colocated with the item-write feature — Create, Clone and Edit share one modal and one
// boundary, deliberately separate from the legacy interfaces under vendor/interface/.

/** GET /lookups/manufacturers */
export interface ILookupManufacturer {
  id: string;
  name: string;
  /** The manufacturer's Logo media as stored — a RELATIVE path, the same value legacy put on
   *  each row as `manufacturerLogoUrl` — or null when it has none (HUBX-8489). The logo is
   *  manufacturer-level reference data, resolved by `manufacturerId` like the name. */
  logoUrl?: string | null;
}

/** GET /lookups/categories — a tree; leaf nodes have no children. */
export interface ILookupCategory {
  categoryId: string;
  name: string;
  children?: ILookupCategory[];
}

export interface ILookupAttributeValue {
  code: string;
  value: string;
}

/** GET /lookups/attributes — e.g. Condition / Packaging / Warranty. */
export interface ILookupAttribute {
  id: string;
  label: string;
  values: ILookupAttributeValue[];
}

/** GET /lookups/exw-points */
export interface ILookupExwPoint {
  id: number;
  description: string;
}

/** Attribute selection sent on create: the lookup attribute id + the selected value CODE
 *  (the domain persists AttributeId + ValueCode — not the display value). */
export interface ICreateItemAttribute {
  attributeId: string;
  value?: string;
}

/** Quantity-break price pair (DQ #1–3). Client rule: fromQty >= 2 (qty 1 is the base price). */
export interface ICreateItemDiscountQty {
  fromQty: number;
  price: number;
}

/**
 * POST /items body — mirror of CreateItemRequest (hubx.item.api).
 * Optional members must be OMITTED when not applicable, never sent as null:
 * the edge treats a present-but-empty SpecialNotes/Restrictions as a 400 (rule 5),
 * and absent HubxItemCode/MinimumOrderQty take server-side defaults.
 * Status axes are never sent (ADR-0009 rule 5); createApproved is the only
 * status-related input and only ADMIN/VM may assert it.
 */
export interface ICreateItemRequest {
  vendorItemCode: string;
  mpn: string;
  description: string;
  manufacturerId: string;
  categoryId: string;
  hubxItemCode?: string;
  /**
   * The company to create for (ADR-0010). The client sends its raw selection; the
   * server decides whether it is a real retarget against the caller's authoritative
   * context and collapses a value equal to that context to "no retarget". Absent
   * (SELLER, or the operator's selection never resolved) means the ambient vendor.
   * Operator roles only: a SELLER supplying a DIFFERING company is rejected with
   * TARGET_VENDOR_NOT_ALLOWED, never ignored.
   */
  targetVendorId?: string;
  unitPrice?: number;
  unitPurchasePrice?: number;
  availability: number;
  minimumOrderQty?: number;
  maximumOrderQty?: number;
  leadTimeDays: number;
  exwId: number;
  createApproved?: boolean;
  specialNotes?: string;
  restrictions?: string;
  attributes: ICreateItemAttribute[];
  discountQtys?: ICreateItemDiscountQty[];
  tags?: string[];
}

/**
 * GET /items/{id} response — the canonical item resource (backend ItemResponse,
 * HUBX-8389 / design doc §8.3). Carries every Create input with ids already
 * resolved (no client-side name matching) so the Create modal can be seeded for
 * Clone (§4.3). Clone uses a SUBSET: it ignores the status axes, effectiveStatus
 * and the rowVersion tokens — those exist for Edit (HUBX-8367). attributes,
 * discountQtys and tags reuse the Create shapes; tags surfaced here are
 * vendor-editable only (IsManual = 0). JSON is camelCase; rowVersion tokens are
 * base64 strings.
 */
export interface ItemResponse {
  id: string;
  itemId: string;
  vendorItemCode: string;
  hubxItemCode?: string | null;
  mpn?: string | null;
  description?: string | null;
  manufacturerId?: string | null;
  /** The RAW per-vendor override — null means "no override", which is NOT the same as
   *  "same as the master". Echo it back verbatim; the effective value for display is
   *  `categoryId ?? masterCategoryId` (ADR-0007, §8.3). */
  categoryId?: string | null;
  /** The referenced master's category, read-only. Never echoed back. */
  masterCategoryId?: string | null;
  /** OMITTED for a Seller, not nulled (§8.3): the sale price would disclose HUBX's margin
   *  on the vendor's own item. Absent and null are different facts — null means "not priced
   *  yet", which a Seller-created item legitimately emits to an operator. */
  unitPrice?: number | null;
  unitPurchasePrice?: number | null;
  availability: number;
  minimumOrderQty: number;
  maximumOrderQty?: number | null;
  leadTimeDays?: number | null;
  exwId: number;
  specialNotes?: string | null;
  restrictions?: string | null;
  attributes: ICreateItemAttribute[];
  /** Omitted for a Seller, same rule as `unitPrice`. */
  discountQtys?: ICreateItemDiscountQty[];
  /** The margin, in percent, a discount tier's price must keep over the cost on this item
   *  (HUBX-8466): the third-party floor, so the tier can be checked while it is typed, as
   *  legacy did. OMITTED when no floor applies: a HUBX-owned item, or a Seller, who sees no
   *  tiers. Read-only, never echoed; the server's 400 stays the authority. */
  discountQtyMarginPercent?: number;
  tags: string[];
  systemStatus: string;
  vendorStatus: string;
  businessStatus: string;
  effectiveStatus: string;
  /** Whether the item is archived (HUBX-8438). A WRITE member like `todaysDeals`, so it must
   *  round-trip — but unlike it, REQUIRED on the way back: omitting it is a 400, not a
   *  "leave it alone".
   *
   *  Not recoverable from `effectiveStatus`, which collapses this flag with the three axes:
   *  `Archived` there hides what the axes say, and any other value hides that the flag is
   *  off. Both travel for that reason. */
  isArchived: boolean;
  /** Whether the item is in the Today's Deals promotion. Unlike the other read members this
   *  is a WRITE member of the Edit body and must round-trip: only MOVING it is operator-owned
   *  (TODAYS_DEALS_NOT_ALLOWED), and that gate is diff-aware, so echoing it is never a change. */
  todaysDeals: boolean;
  /** Which fields THIS caller may write on THIS item (ADR-0011). Always present. Read-only —
   *  dropped from the echo. A CEILING, not a guarantee: a field listed here is one no
   *  permission rule blocks, but value-level validation still applies.
   *
   *  `manufacturerId` and `mpn` NEVER appear, for any caller: their permission depends on a
   *  destination master the user has not chosen yet. `GET /items/resolutions` answers those
   *  two per keystroke, which is why that endpoint is load-bearing for the Edit form rather
   *  than advisory to it. `vendorItemCode` never appears either — immutable for everyone.
   *
   *  Attribute sub-members travel as paths under the attribute NAME
   *  (`attributes.Condition`, plus `attributes.*` for the free-form remainder), resolved to
   *  ids through the `/lookups/attributes` catalog. */
  writableFields: string[];
  rowVersion: string;
  itemRowVersion: string;
}

/**
 * PUT /items/{id} body — mirror of EditItemRequest (hubx.item.api).
 *
 * FULL STATE, not a delta: every member travels on every request whether the user touched
 * it or not, and the server diffs against stored state. An omitted required member is a
 * real removal, not "leave it alone".
 *
 * It is the GET response re-submitted verbatim, minus the read-only members
 * (`id`, `itemId`, `masterCategoryId`, `systemStatus`, `vendorStatus`, `businessStatus`,
 * `writableFields`), with the two tokens renamed to their `expected*` forms and
 * `effectiveStatus` renamed to `status` (ADR-0009 Rule 9). That round trip must produce an
 * EMPTY delta — anything the client has to fix up or strip before sending breaks it.
 *
 * Not accepted: `targetVendorId` (an existing item's vendor is not retargetable, ADR-0010),
 * `createApproved` (Create-only intent), and any acknowledgment flag.
 */
export interface IEditItemRequest {
  vendorItemCode: string;
  mpn: string;
  description: string;
  manufacturerId: string;
  categoryId: string;
  hubxItemCode?: string;
  unitPrice?: number;
  unitPurchasePrice?: number;
  availability: number;
  minimumOrderQty?: number;
  maximumOrderQty?: number;
  leadTimeDays: number;
  exwId: number;
  specialNotes?: string;
  restrictions?: string;
  attributes: ICreateItemAttribute[];
  discountQtys?: ICreateItemDiscountQty[];
  tags?: string[];
  /** The status intent (ADR-0009 Rule 9): an EffectiveStatus NAME — the value picked in the
   *  collapsed Status control, or the `effectiveStatus` read when it was left alone. The
   *  server, not the client, decides which axis it moves (un-pause, approval, manual
   *  reactivation). Only `Active` and `Paused` carry an intent; any other name is an echo. */
  status: string;
  /** Whether the item is archived (HUBX-8438). Full-state and REQUIRED — omitting it is a 400,
   *  deliberately: legacy's equivalent defaults to false and is compared against stored state,
   *  so a client that leaves it out silently un-archives the item. Echoing the value read is
   *  never a change; every interactive role may move it, Seller included. */
  isArchived: boolean;
  /** The two tokens the client read, base64. Both are required unconditionally: whether a
   *  master write happens is the server's conclusion, not the client's to predict. */
  expectedRowVersion: string;
  expectedItemRowVersion: string;
  /** The ONE member here that is not full-state: ABSENT means "do not touch". A Seller's body
   *  never carries it — omit it rather than sending the value read, since a Seller that moves
   *  it is rejected. Absence must not mean removal: an ordinary save would otherwise wipe a
   *  promotion marketing put on the item. */
  todaysDeals?: boolean;
}

/** PUT /items/{id} 200 body (backend ItemEditResponse). No Location header — the resource
 *  did not move. `rowVersion` is the ROTATED token, except on a write that changed nothing,
 *  where the submitted one comes back unchanged and that identity is the signal. */
export interface IEditItemResult {
  id: string;
  itemId: string;
  vendorItemCode: string;
  hubxItemCode?: string | null;
  rowVersion: string;
  itemRowVersion?: string | null;
  warnings: IWriteWarning[];
  commandId: string;
}


/**
 * A non-blocking advisory on a SUCCESSFUL write (backend WriteWarning, design doc
 * §8.1). Never alters the 201/200; `code` is the stable contract and `data` is a
 * flat bag whose keys are part of the contract for that code. New advisories add a
 * code, so consumers must match on `code` and ignore what they don't know.
 */
export interface IWriteWarning {
  code: string;
  data?: { [key: string]: string };
}

/** The `code` the per-vendor category divergence is published under (HUBX-8428);
 *  its `data.categoryId` is the category the shared item is filed under. */
export const CATEGORY_DIFFERS_FROM_MASTER = 'CATEGORY_DIFFERS_FROM_MASTER';

/** POST /items 201 body (backend ItemCreateResponse). Only `warnings` is consumed
 *  today — the id comes from the Location header — but the shape is typed in full
 *  so a future reader is not tempted to re-derive it. */
export interface IItemCreateResponse {
  id: string;
  itemId: string;
  itemWasCreated: boolean;
  vendorItemCode: string;
  hubxItemCode: string;
  rowVersion: string;
  itemRowVersion?: string | null;
  warnings: IWriteWarning[];
  commandId: string;
}

/** Outcome of a successful create: 201 + Location: /items/{id}, plus the write-ack
 *  advisories (empty, never null — HUBX-8428). */
export interface ICreateItemResult {
  id: string;
  location: string;
  warnings: IWriteWarning[];
}

/**
 * GET /items/resolutions?mpn={mpn} — whether an MPN already resolves to an existing
 * item (HUBX-8430), feeding the pre-submit category nudge (HUBX-8431).
 *
 * `categoryId` is that item's category and is nullable EVEN WHEN `resolved` is true:
 * a legacy item may carry none, and then there is nothing to compare against. The
 * server always answers 200 — `resolved: false` means the MPN would create a new
 * item, which is an answer, not an error.
 */
export interface IItemResolution {
  resolved: boolean;
  itemId?: string | null;
  categoryId?: string | null;
}

/**
 * What the Clone header says about the item the modal was seeded FROM
 * (HUBX-8422). Assembled from the list row, never from ItemResponse: the read
 * representation is the edit contract and carries no activity dates and no
 * seller — and must not grow them just to feed a header.
 *
 * `id` is what the parent actually fetches with; the rest is display only, and
 * every display member is optional because the row supplies them conditionally
 * (a Seller sees no company name; an item that never went live has no launch
 * date). Absent members are omitted from the strip rather than rendered blank.
 */
export interface ICloneSource {
  id: string;
  vendorItemCode: string;
  companyName?: string | null;
  launchedOn?: string | Date | null;
  updatedOn?: string | Date | null;
}

/**
 * The Edit header band (HUBX-8388): what the item IS, beside the form that changes it.
 * Display only — nothing here is read by the form or by the request.
 *
 * Sourced from the GRID ROW, not from GET /items/{id} (HUBX-8436 §6): these are
 * read-model facets, and ItemResponse is the EDIT contract — every member of it is
 * something the PUT accepts back. Putting read-only decoration there would blur that
 * line, and the row already carries all of it.
 *
 * The parent builds this, so the modal never has to know the legacy row shape
 * (IVendorItemInfo) — the same boundary ICloneSource draws for Clone.
 *
 * TWO promo badges, not three, and not the four the legacy popup draws:
 * - Today's Deals is absent BY DESIGN. It is a real write member of the PUT, so it is a
 *   form control seeded from `target.todaysDeals` — one fact, one source. Carrying it
 *   here too would mean the badge and the control could disagree.
 * - The fourth legacy badge ("New", bell icon) has had no backend writer since it was
 *   introduced in 2018 (HUBX-2129) and its active/inactive assets were swapped at birth,
 *   so it has always rendered the wrong way round on a value nothing sets.
 *
 * What remains are the two the service bus writes and v2 never does — informational.
 */
export interface IEditHeader {
  /** Curation facets, each true when the row carries the matching attribute. Names are
   *  the display LABELS the row uses — Price Drop / Just Launched — which diverge from
   *  the AttributeName the write side matches on (Price Drop / Newly Added). */
  priceDrop: boolean;
  justLaunched: boolean;
  /** `lastActivatedOn` — absent on an item that never went live. */
  launchedOn?: string | Date | null;
  /** `lastUpdatedOn` — absent on a row never edited since it was loaded. */
  updatedOn?: string | Date | null;
}

/** RFC 9457 ProblemDetails as emitted by the v2 API; `code` is the stable
 *  extension member the UI routes errors by. `errors` carries per-member
 *  validation failures on 400 (ASP.NET ValidationProblemDetails shape). */
export interface IItemProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  errors?: { [member: string]: string[] };
  /** The figures some codes carry, as invariant-culture strings (ProblemDetailsMapping);
   *  absent when the code has none. Only REACTIVATION_THRESHOLD_NOT_MET's are read
   *  (`reactivationThreshold`), to name the threshold in its line (item-write-messages). */
  data?: { [key: string]: string };
  /** On a 412 only: the item exactly as the server currently holds it — the SAME shape
   *  `GET /items/{id}` returns (HUBX-8439), so the conflict is rendered and re-merged with
   *  the code that already renders the form, and the retry is the same echo the form
   *  already builds. It carries BOTH tokens, which is what makes a retry possible at all,
   *  and `writableFields` scoped to this caller.
   *
   *  Absent when the server could not re-read the item (deleted between the write and the
   *  re-read, or the caller lost read scope). `code` is the contract; this is the courtesy —
   *  a client must still handle the 412 without it. */
  currentState?: ItemResponse;
}

/**
 * One member the write-conflict comparison covers (HUBX-8439). `writable` is the
 * ADR-0011 path deciding whether the member may travel in THIS caller's body at all.
 * Absent, or absent from `writableFields`, means the resolve popup offers the member as
 * Current only (HUBX-8466) — sending a value the caller may not move turns the next Save
 * into a 403.
 */
export interface IConflictField {
  key: string;
  label: string;
  control?: string;
  writable?: string;
}

/**
 * A member the other writer moved (HUBX-8439). Presence is the entire payload: a mark
 * puts a ring on its control and nothing else.
 *
 * It carries no values and no answer. The two-value block with Keep mine / Take theirs
 * that used to sit under each control was dropped (2026-08-31): in the 64px numeric
 * columns it wrapped to one digit per line. The per-field question is asked in the resolve
 * popup (HUBX-8466), which lists every member where the screen differs from the item as
 * re-read; the ring only says where the other writer landed.
 */
export interface IConflictMark {
  key: string;
  label: string;
  control?: string;
}

/**
 * The error codes an Edit can return that the UI routes on. Anything not listed is shown as
 * a generic failure — the catalog grows server-side, so an unknown code must never crash the
 * form.
 */
export const EDIT_ERROR_CODES = {
  /** 412. The stored row moved since it was read. Carries `currentState`. */
  concurrencyConflict: 'CONCURRENCY_CONFLICT',
  /** 409. The write would change state SAP owns on a HUBX-owned item. `data.field` names the
   *  offender, in the REQUEST's vocabulary (`availability`, not the column name). Only a
   *  curation-only change is permitted on such an item. */
  itemNotPortalEditable: 'ITEM_NOT_PORTAL_EDITABLE',
  /** 409. The submitted code diverges from the stored one. Changing a code is a Clone plus a
   *  kill, not an Edit. */
  vendorItemCodeImmutable: 'VENDOR_ITEM_CODE_IMMUTABLE',
  /** 403. A Seller tried to change the external id. */
  hubxItemCodeNotAllowed: 'HUBX_ITEM_CODE_NOT_ALLOWED',
  /** 403. A caller moved a status axis outside its rights. */
  statusTransitionNotAllowed: 'STATUS_TRANSITION_NOT_ALLOWED',
  /** 403. A Seller changed Condition/Packaging/Warranty past `Pending`. */
  requiredAttributeChangeNotAllowed: 'REQUIRED_ATTRIBUTE_CHANGE_NOT_ALLOWED',
  /** 403. A Seller MOVED the Today's Deals flag. Echoing it is fine — only moving is gated. */
  todaysDealsNotAllowed: 'TODAYS_DEALS_NOT_ALLOWED',
  /** 409. The master is shared, so this manufacturer change is refused. `data.field`. */
  masterFieldForbidden: 'MASTER_FIELD_FORBIDDEN',
  /** 403. Not in this role at this status — manufacturer or MPN. `data.field`. */
  masterFieldChangeNotAllowed: 'MASTER_FIELD_CHANGE_NOT_ALLOWED',
  /** 409. The item already exists in SAP under its mapped code, so the external id is
   *  frozen (HUBX-8458). TERMINAL: SAP never renames an item code, so retrying cannot
   *  succeed and no role is exempt. The only way forward is to put the stored XID back,
   *  which is why this one gets a Revert affordance rather than just a message. */
  hubxItemCodeLockedInB1: 'HUBX_ITEM_CODE_LOCKED_IN_B1',
  /** 503. Whether the item exists in SAP could not be established — B1 unreachable, or no
   *  endpoint configured for this environment (HUBX-8458). TRANSIENT and distinct from the
   *  409 on purpose: nothing was evaluated and the external id was left alone, so pressing
   *  Save again is a real answer. Same shape as TARGET_VENDOR_CHECK_UNAVAILABLE. */
  hubxItemCodeCheckUnavailable: 'HUBX_ITEM_CODE_CHECK_UNAVAILABLE',
} as const;

/** Advisories an Edit 200 can carry, beyond the category divergence shared with Create. */
export const MANUFACTURER_CHANGE_AFFECTS_SIBLINGS = 'MANUFACTURER_CHANGE_AFFECTS_SIBLINGS';
export const MANUFACTURER_ADOPTED_FROM_MASTER = 'MANUFACTURER_ADOPTED_FROM_MASTER';

